<template>
  <div
    :class="$style.swipeActions"
    :style="{ ...swipeActionsStyle, ...swipeActionWidths }"
  >
    <button
      :class="[$style.swipeAction, $style.completeAction]"
      @click.stop="handleSwipeActionClick('complete')"
      @mousedown.stop="handleSwipeActionClick('complete')"
      @touchstart.stop="handleSwipeActionClick('complete')"
    >
      <CheckCircleIcon v-if="task.completed && showIcons" :size="24" />
      <CheckIcon v-else-if="!task.completed && showIcons" :size="24" />
    </button>
    <button
      :class="[$style.swipeAction, $style.editAction]"
      @click.stop="handleSwipeActionClick('details')"
      @mousedown.stop="handleSwipeActionClick('details')"
      @touchstart.stop="handleSwipeActionClick('details')"
    >
      <DetailsIcon v-if="showIcons" :size="20" />
    </button>
    <button
      :class="[$style.swipeAction, $style.deleteAction]"
      @click.stop="handleSwipeActionClick('delete')"
      @mousedown.stop="handleSwipeActionClick('delete')"
      @touchstart.stop="handleSwipeActionClick('delete')"
    >
      <DeleteIcon v-if="showIcons" :size="20" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Task } from "../types/task";
import { CheckIcon, CheckCircleIcon, DetailsIcon } from "./icons";
import DeleteIcon from "./DeleteIcon.vue";

const props = defineProps<{
  task: Task;
  isSwiping: boolean;
  swipeDistance: number;
  maxSwipeDistance: number;
}>();

const emit = defineEmits<{
  (e: "complete"): void;
  (e: "edit"): void;
  (e: "delete"): void;
}>();

// Swipe actions style
const swipeActionsStyle = computed(() => {
  if (!props.isSwiping && props.swipeDistance === 0) {
    return {
      transform: "translateX(100%)",
      "--swipe-distance": "0px",
    };
  }

  const translateX = Math.min(props.swipeDistance, props.maxSwipeDistance);
  return {
    transform: `translateX(${
      100 - (translateX / props.maxSwipeDistance) * 100
    }%)`,
    "--swipe-distance": `${translateX}px`,
  };
});

// Dynamic button widths based on swipe distance
const swipeActionWidths = computed(() => {
  // Always calculate width based on swipe distance, regardless of isSwiping state
  const progress = Math.min(props.swipeDistance / props.maxSwipeDistance, 1);
  const minWidth = 5;
  const maxWidth = 60;
  const width = minWidth + (maxWidth - minWidth) * progress;

  return {
    "--complete-width": `${width}px`,
    "--edit-width": `${width}px`,
    "--delete-width": `${width}px`,
  };
});

// Icon visibility based on button width
const showIcons = computed(() => {
  // Always calculate based on swipe distance for smooth transitions
  const progress = Math.min(props.swipeDistance / props.maxSwipeDistance, 1);
  const minWidth = 5;
  const maxWidth = 60;
  const currentWidth = minWidth + (maxWidth - minWidth) * progress;

  return currentWidth >= 20;
});

// Action handlers
const handleSwipeActionClick = (action: string) => {
  console.log(`SwipeActions: Button clicked with action: "${action}"`);
  console.log(
    `SwipeActions: Current props - isSwiping: ${props.isSwiping}, swipeDistance: ${props.swipeDistance}, maxSwipeDistance: ${props.maxSwipeDistance}`
  );
  console.log(`SwipeActions: showIcons: ${showIcons.value}`);

  switch (action) {
    case "complete":
      console.log("SwipeActions: Emitting 'complete' event");
      emit("complete");
      break;
    case "details":
      console.log("SwipeActions: Emitting 'edit' event");
      emit("edit");
      break;
    case "delete":
      console.log("SwipeActions: Emitting 'delete' event");
      emit("delete");
      break;
    default:
      console.warn(`SwipeActions: Unknown action: "${action}"`);
  }
};
</script>

<style module>
/* Swipe Actions Container */
.swipeActions {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: stretch;
  z-index: 10; /* Above all content including overrun areas */
  pointer-events: none;
  width: 180px; /* 3 buttons × 60px max width */
}

/* Swipe actions are always visible behind content */
.swipeActions {
  transform: translateX(0);
  transition: transform 0.15s ease-out;
}

.swipeAction {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: white;
  font-weight: 600;
  transition: width 0.15s ease-out;
  pointer-events: auto;
  border-radius: 0;
  overflow: hidden;
}

.completeAction {
  width: var(--complete-width, 60px);
  background: linear-gradient(135deg, #00b894, #00a085);
}

.editAction {
  width: var(--edit-width, 60px);
  background: linear-gradient(135deg, #74b9ff, #0984e3);
}

.deleteAction {
  width: var(--delete-width, 60px);
  background: linear-gradient(135deg, #9079fd, #e84393);
}

.swipeAction:hover {
  filter: brightness(1.1);
}

.swipeAction:active {
  transform: scale(0.95);
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .swipeAction {
    min-width: 5px;
  }
}

@media (max-width: 480px) {
  .swipeAction {
    min-width: 5px;
  }
}
</style>
